import React from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import api from '@tutorbot/directory-api';
import { LocalizationContext } from '../../hoc/Localization';
import { LocationContext } from '../../hoc/Location';
import { SettingsContext } from '../../hoc/Settings';
import TitleContent from '../../components/TinyComponents/TitleContent';
import Community from '../../components/Community';
import Header from '../../components/LandingComponents/Header';
import BlogHeader from '../../components/CardList/Header';
import Join from '../../components/LandingComponents/Join';
import Container from '../../components/Home/Container';
import Subscribe from '../../components/Home/Subscribe';
import CardList from '../../components/CardList';
import { getCourses } from '../../utils/directory';
import { getCategoryPaths } from '../../utils/api';
import { cleanString } from '../../utils';
import Blog from '../../components/Home/Blog';
import styles from './styles.less';
import JoinGroup from '../../components/LandingComponents/JoinGroup';
import Professions from '../../components/Paths/pathNew/Professions';
import Error from '../_error';

const Landing = ({ data, category: categoryData, type, categoryName }) => {
  const { t, locale, blogLocale, translate } = React.useContext(LocalizationContext);
  const { countries } = React.useContext(LocationContext);
  const {
    domainSettings: { blogCountry, featureCommunity, domain },
  } = React.useContext(SettingsContext);

  const [blog, setBlog] = React.useState([{}, {}, {}]);
  const [courses, setCourses] = React.useState(data);
  const [discounted, setDiscounted] = React.useState([{}, {}, {}, {}, {}, {}]);
  const [category, setCategory] = React.useState(categoryData);
  const [paths, setPaths] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      if (!category || !Object.keys(category).length) {
        setCategory(await api().categories.where('label', categoryName).first());
      }
    })();
  }, []);

  React.useEffect(() => {
    if (category && Object.keys(category).length) {
      (async () => {
        setCourses(
          (
            await getCourses({
              filters: { countries, category: category.label },
              limit: 6,
            })
          ).data,
        );
        setDiscounted(
          (
            await getCourses({
              filters: { countries, category: category.label, type: 'discounted' },
              limit: 6,
            })
          ).data,
        );
        setPaths(await getCategoryPaths(domain, category.id));
      })();
    }
  }, [category]);

  React.useEffect(() => {
    if (category && Object.keys(category).length) {
      (async () => {
        setBlog(
          (
            await api()
              .blog.limit(3)
              .where('categories', blogCountry)
              .where('categories', blogLocale)
              .where('categories', category.blogCategory)
              .sort({ date: 'desc' })
              .get()
          ).data,
        );
      })();
    }
  }, [blogLocale, category]);

  if (!category) {
    return <Error />;
  }

  return (
    <div className={styles.container}>
      <Head>
        <title>
          {`${translate(category, 'title')} | ${translate(category, 'name')} | ${t(
            'meta.title.suffix',
          )}`}
        </title>
        <meta name="description" content={translate(category, 'description')} />
        <meta name="keywords" content={translate(category, 'keywords')} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={translate(category, 'title')} />
        <meta property="og:description" content={cleanString(translate(category, 'content'))} />
        <meta property="og:image" content={category.imageUrl} />
        <meta property="og:image:width" content="600" />
        <meta property="og:image:height" content="450" />
        <meta property="og:locale" content={locale} />
      </Head>

      <Container>
        <Header category={category} />
      </Container>

      <Container>
        <TitleContent
          title={<h2>{t('landing.title.courses')}</h2>}
          content={<CardList data={courses} filters={{ category: category.label }} />}
        />
      </Container>
      {type && ['english', ' german'].includes(type) ? (
        <Container>
          <JoinGroup category={category} />
        </Container>
      ) : (
        <Container className={styles.join}>
          <Join
            text={translate(category, 'join_text')}
            link={category[`join_link_${countries[0]}`]}
            forumLink={category.forumLink}
            category={category}
          />
        </Container>
      )}
      {!!discounted.length && (
        <Container>
          <TitleContent
            title={<h2>{t(`frontpage.courses.discount.${category.label}.title`)}</h2>}
            content={
              <CardList
                data={discounted}
                filters={{ category: category.label, discountedPrice: true }}
              />
            }
          />
        </Container>
      )}

      {!!paths && !!paths.length && (
        <Container>
          <h2>{t('courses.professions')}</h2>
          <div className={styles.professions}>
            {paths.map(p => (
              <Professions
                key={p.id}
                id={p.id}
                image={p.image}
                title={p[`title_${locale}`]}
                profession={p.profession[`title_${locale}`]}
                description={p[`description${locale}`] || p.description}
              />
            ))}
          </div>
        </Container>
      )}

      {!!translate(category, 'about_title') && !!translate(category, 'about_text') && (
        <Container>
          <TitleContent
            content={
              <div className={styles.content}>
                <h2 className={styles.contentTitle}>{translate(category, 'about_title')}</h2>

                <p className={styles.contentText}>{translate(category, 'about_text')}</p>
              </div>
            }
          />
        </Container>
      )}

      <div>
        <div className={styles.subscribeDiv} id="subscribe" />
        <Container className={styles.subscribe}>
          <Subscribe categories={[category && category.label]} />
        </Container>
      </div>

      {!!blog.length && (
        <Container>
          <TitleContent
            title={
              <div className={styles.content}>
                <div className={styles.flex}>
                  <h2 className={styles.contentTitle}>{t('landing.title2')}</h2>
                  <div className={styles.showMore}>
                    <BlogHeader
                      link
                      linkOptions={{
                        route: 'blogCategory',
                        category: category.label,
                      }}
                    />
                  </div>
                </div>
                <span className={styles.contentText}>{translate(category, 'articlesContent')}</span>
              </div>
            }
            content={<Blog articles={blog} />}
          />
        </Container>
      )}
      {featureCommunity && !!category.communityCategory && (
        <Community param={category.communityCategory} />
      )}
    </div>
  );
};

Landing.propTypes = {
  data: PropTypes.array,
  categoryName: PropTypes.string,
  type: PropTypes.string.isRequired,
  category: PropTypes.object.isRequired,
};

Landing.defaultProps = {
  categoryName: '',
  data: [{}, {}, {}, {}, {}, {}],
};

Landing.getInitialProps = async (
  { res, query: { category } },
  router,
  { locationProps: { locations } },
) => {
  const countries = locations
    .filter(location => location.default)
    .map(location => location.country);

  const { data } = await getCourses({ filters: { category, countries }, limit: 6 });

  const categoryData = await api().categories.where('label', category).first();

  if (!data || !categoryData) {
    res.statusCode = 404;
  }

  return {
    data,
    category: categoryData,
    categoryName: category,
  };
};

export default Landing;
